import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import TrafficList from "./TrafficList";
import useNetworkData from "../data/useNetworkData";
import useEventsMonioringData from "../data/useEventsMonioringData";
import TrafficMonitoringMap from "./trafficMonitoringMap/TrafficMonitoringMap";
import useTrafficMonitoringGeom from "../data/useTrafficMonitoringGeom";
import MetroStatusCard from "layouts/trafficmonitoring/infohub/components/MetroStatusCard";

// Memoized components to prevent unnecessary remounts
const MemoTrafficList = React.memo(TrafficList);
const MemoTrafficMonitoringMap = React.memo(TrafficMonitoringMap);

function NetworkView() {
  const { t } = useTranslation();
  // Hooks: state, refs, memos, effects
  const { data, loading } = useNetworkData();
  const { events, loading: eventsLoading, error } = useEventsMonioringData();
  const {
    data: networkData,
    loading: networkLoading,
  } = useTrafficMonitoringGeom();

  const hasLoadedOnce = useRef(false);

  const [onlyActive, setOnlyActive] = useState([]);
  const [onlyJam, setOnlyJam] = useState([]);
  const [networkSource, setNetworkSource] = useState([]);

  // Derive active & jam events
  useEffect(() => {
    if (Array.isArray(events) && events.length > 0) {
      setOnlyActive(events);
      setOnlyJam(events.filter((e) => e.category?.id === 10));
    }
  }, [events]);

  // Update GeoJSON source for the map
  useEffect(() => {
    if (networkData) {
      setNetworkSource(networkData);
    }
  }, [networkData]);

  // Memoize statistics arrays to keep prop identity stable
  const dataOfEvents = useMemo(
    () => [
      {
        name: t("trafficMonitoring.monitoring.metrics.numberOfEvents.name"),
        values: [
          { label: t("trafficMonitoring.monitoring.metrics.labels.total"), value: onlyActive.length, status: "neutral" },
        ],
        description: t("trafficMonitoring.monitoring.metrics.numberOfEvents.descriptionNetwork"),
      },
    ],
    [onlyActive.length, t]
  );

  const dataOfJams = useMemo(
    () => [
      {
        name: t("trafficMonitoring.monitoring.metrics.numberOfJams.name"),
        values: [{ label: t("trafficMonitoring.monitoring.metrics.labels.total"), value: onlyJam.length, status: "neutral" }],
        description: t("trafficMonitoring.monitoring.metrics.numberOfJams.descriptionNetwork"),
      },
    ],
    [onlyJam.length, t]
  );

   // **Combine all three into one array**
  const allMetrics = useMemo(
    () => [...(data || []), ...dataOfEvents, ...dataOfJams],
    [data, dataOfEvents, dataOfJams]
  );



  // Loading guard: only on first load
  const anyLoading = loading || eventsLoading || networkLoading;
  if (anyLoading && !hasLoadedOnce.current) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{ height: "100%" }}
      >
        <CircularProgress />
      </Grid>
    );
  }
  hasLoadedOnce.current = true;

  // Render
return (
  <Grid
    container
    spacing={2}
    sx={{
      height: "100%",    
       minHeight: "80vh",
    }}
  >
    {/* ==== LEFT SIDEBAR (30%) ==== */}
    <Grid item xs={12} md={3}>
      <MDBox
        sx={{
           height: { xs: "auto", md: "100%" },
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Scrollable metrics list */}
        <MDBox
          sx={{
            mb:1,
            flexGrow: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <MemoTrafficList data={allMetrics} />
          <MetroStatusCard />
        </MDBox>
      </MDBox>
    </Grid>

    {/* ==== RIGHT MAP (70%) ==== */}
    <Grid item xs={12} md={9}>
      <MDBox
        sx={{
           height: { xs: "420px", md: "100%" },
          position: "relative",
        }}
      >
        <MemoTrafficMonitoringMap
          onlyActive={onlyActive}
          networkSource={networkSource}
          areaRectSource={[]}
        />
      </MDBox>
    </Grid>
  </Grid>
);


}

export default NetworkView;
